import { defaultTo, get } from 'lodash';
import * as React from 'react';
import { ICommissionBreakdownObject } from '../../../../data/entities/commission-breakdown';
import { IBookingBreakdown, IBookingExtraItems, IBookingV2 } from '../../../../data/entities/event/booking';
import { IAppliedPromotion } from '../../../../data/entities/promotions';
import { currencySymbolForCurrencyString } from '../../../../view/helpers/currency_helpers';
import { calculateDiscrepancyRate } from '../../../pages/property/bookings/details/helpers';
import { Anchor } from '../../anchor/Anchor';
import { FormInputSymbol } from '../../form/form-input-symbol/FormInputSymbol';
import { InfoPill } from '../../info-pill/InfoPill';
import { Content } from '../../page/content/Content';
import { Panel } from '../../panel/Panel';
import { Table, Tbody, Td, Th, Thead, Tr } from '../../table';
import {
	bookingHas2021CommissionValues,
	getBookingCommissionRows,
	getCleaningFee,
	getExtraFeesAmount,
	presentationValueForCommission
} from './helpers';

export interface IFinanceBreakdownProps {
	booking: IBookingV2;
	commissionBreakdown: ICommissionBreakdownObject;
}

export interface IFinanceBreakdownRow {
	name: string;
	info?: string;
	percentage?: number;
	net?: number;
	vat?: number;
	gross?: number;
	prominent?: boolean;
}

export interface IFinanceCommissionBreakdownRow {
	name: string;
	info?: string;
	// Indicates this row displays a rate instead of a number
	is_rate: boolean;
	travelnest_fee: number | null;
	channel_fee: number | null;
	adjustment_fee: number | null;
	vat: number | null;
	total_fee: number;
}

export const FinanceBreakdown: React.FunctionComponent<IFinanceBreakdownProps> = (props) => {
	const breakdown = props.booking.value ? props.booking.value.breakdown : {} as IBookingBreakdown;
	const appliedPromotions = props.booking.owner_promotional_discounts || [];

	const promotions = props.booking.owner_promotional_discounts || [];

	const currencySymbol = currencySymbolForCurrencyString(props.booking.value.currency);

	// Extract Information from breakdown
	const ownerExtras = get(breakdown, 'owner.extras.items', []) as IBookingExtraItems[];
	const extraFees = getExtraFeesAmount(ownerExtras, []);
	const cleaningFee = getCleaningFee(ownerExtras);
	const cityTax = get(breakdown, 'owner.city_tax', 0);
	const cityTaxIncluded = get(breakdown, 'owner.city_tax_included', false);
	const ownerRent = get(breakdown, 'owner.rent_value', 0);
	const discountedOwnerRent = get(breakdown, 'owner.discounted_owner_rent_value', ownerRent);

	const tbv = props.booking.value.total || 0;

	const effectiveTbv = get(breakdown, 'channel.effective_total_booking_value', 0);

	const guestPaymentValue = get(breakdown, 'channel.guest_payment_value') as number;
	const convertedFromGuestPaymentValue = guestPaymentValue / get(breakdown, 'channel.converted_cash_receipt_fx_rate');

	// Finance always wants the "original" values for discrepancy and booking fee to be displayed
	// so we use the values in the payload if there aren't any post payments, otherwise we work backwards
	const hasPostPayments = !!get(props.booking, 'post_payments', []).length;

	const displayBookingFeeGross = hasPostPayments ?
		(discountedOwnerRent + extraFees) * get(breakdown, 'travelnest.expected_booking_fee_rate') :
		get(breakdown, 'travelnest.expected_booking_fee_value');

	const displayBookingFeeNet = hasPostPayments ?
		displayBookingFeeGross / (1 + get(breakdown, 'travelnest.vat_rate')) :
		get(breakdown, 'travelnest.expected_booking_fee_net_value');

	const displayBookingFeeVat = hasPostPayments ?
		displayBookingFeeGross - displayBookingFeeNet :
		get(breakdown, 'travelnest.expected_booking_fee_vat_value');

	const displayDiscrepancyGross = hasPostPayments ?
		tbv - (discountedOwnerRent + extraFees) - displayBookingFeeGross :
		get(breakdown, 'travelnest.booking_discrepancy');

	const displayDiscrepancyNet = hasPostPayments ?
		displayDiscrepancyGross / (1 + get(breakdown, 'travelnest.vat_rate')) :
		get(breakdown, 'travelnest.booking_discrepancy_net_value');

	const displayDiscrepancyVat = hasPostPayments ?
		displayDiscrepancyGross - displayDiscrepancyNet :
		get(breakdown, 'travelnest.booking_discrepancy_vat_value');

	const renderAppliedPromotions = (applied: IAppliedPromotion[]) => {
		const discounts = applied.map((promotion) => {
			return {
				name: promotion.internal_reference.replace(/_/g, ' '),
				gross: promotion.discount_amount! * -1
			};
		});
		return [
			{
				name: 'Discounted owner rent',
				gross: get(breakdown, 'owner.discounted_owner_rent_value', 0)
			}
		].concat(discounts!);
	};

	const appliedPromotionsArr = renderAppliedPromotions(promotions);

	const rows = [
		{
			name: 'Undiscounted owner rent',
			gross: get(breakdown, 'owner.rent_value', 0)
		},
		{
			name: 'Cleaning fee',
			gross: cleaningFee
		},
		{
			name: 'Booking uplift',
			percentage: defaultTo(get(breakdown, 'travelnest.expected_booking_fee_rate'), 0) * 100,
			net: defaultTo(displayBookingFeeNet, 0),
			vat: defaultTo(displayBookingFeeVat, 0),
			gross: defaultTo(displayBookingFeeGross, 0)
		},
		{
			name: 'Discrepancy',
			percentage: calculateDiscrepancyRate(defaultTo(displayDiscrepancyGross, 0), tbv) * 100,
			net: defaultTo(displayDiscrepancyNet, 0),
			vat: defaultTo(displayDiscrepancyVat, 0),
			gross: defaultTo(displayDiscrepancyGross, 0)
		},
		{
			name: ''
		},
		{
			name: 'Total booking value',
			info: 'Original value of the booking',
			gross: tbv,
			prominent: true
		},
		{
			name: 'Effective booking value',
			info: 'Value of the booking after exceptional payments have been applied',
			gross: effectiveTbv
		},
		{
			name: ''
		},
		{
			name: ''
		},
		{
			name: 'Owner payment due',
			gross: defaultTo(get(breakdown, 'owner.payment_value'), 0),
			prominent: true
		},
		{
			name: ''
		},
		{
			name: 'Guest charge',
			gross: convertedFromGuestPaymentValue
		},
		// Padding bottom of finance from commission with these 2 empty rows
		{
			name: ''
		},
		{
			name: ''
		}
	] as IFinanceBreakdownRow[];

	if (cityTax) {
		rows.splice(1, 0, {
			name: `City tax ${cityTaxIncluded ? '(included)' : '(excluded)'}`,
			gross: cityTax
		});
	}

	if (promotions !== undefined && promotions.length !== 0) {
		appliedPromotionsArr?.forEach((discount) => rows.splice(1, 0, discount) as IFinanceBreakdownRow[]);
	}

	const commissionRows: IFinanceCommissionBreakdownRow[] = getBookingCommissionRows(
		props.booking,
		props.commissionBreakdown,
		appliedPromotions);

	return <Content type="full">
		<h2>Finance booking breakdown and payment</h2>
		<Anchor id="breakdown" />
		<Table className="booking-breakdown-internal">
			<Thead>
				<Tr>
					<Th>Item</Th>
					<Th>%</Th>
					<Th>{currencySymbol} Net</Th>
					<Th>{currencySymbol} VAT</Th>
					<Th>{currencySymbol} Gross</Th>
				</Tr>
			</Thead>
			<Tbody>
				{rows.map((row, i) => <Tr key={i}>
					<Td className="booking-breakdown__table-cell--wide">{row.name}{row.info ? <> <InfoPill message={row.info} /></> : ''}</Td>
					<Td className="booking-breakdown__table-cell--value">
						{row.percentage !== undefined ? <FormInputSymbol
							symbol="%"
							position="after"
							value={(row.percentage || 0).toFixed(1)}
							readOnly={true}
							prominent={row.prominent}
						/> : ''}
					</Td>
					<Td className="booking-breakdown__table-cell--value">
						{row.net !== undefined ? <FormInputSymbol
							symbol={currencySymbol}
							value={(row.net || 0).toFixed(2)}
							readOnly={true}
							prominent={row.prominent}
						/> : ''}
					</Td>
					<Td className="booking-breakdown__table-cell--value">
						{row.vat !== undefined ? <FormInputSymbol
							symbol={currencySymbol}
							value={(row.vat || 0).toFixed(2)}
							readOnly={true}
							prominent={row.prominent}
						/> : ''}
					</Td>
					<Td className="booking-breakdown__table-cell--value">
						{row.gross !== undefined ? <FormInputSymbol
							symbol={currencySymbol}
							value={(row.gross || 0).toFixed(2)}
							readOnly={true}
							prominent={row.prominent}
						/> : ''}
					</Td>
				</Tr>)}
			</Tbody>
		</Table>
		{!bookingHas2021CommissionValues(breakdown) && <Panel withIcon={true} type="warn">
			This booking was made using the previous commission model, so not all values are present
		</Panel>}
		<Table className="booking-breakdown-internal commission">
			<Thead>
				<Tr>
					<Th>Item</Th>
					<Th>TN Fee</Th>
					<Th>Channel Fee</Th>
					<Th>Commission Adjustments</Th>
					<Th>VAT</Th>
					<Th>Total Fee + VAT</Th>
				</Tr>
			</Thead>
			<Tbody>
				{commissionRows.map((row, i) => <Tr key={i}>
					<Td className="booking-breakdown__table-cell--wide">{row.name}{row.info ? <> <InfoPill message={row.info} /></> : ''}</Td>
					<Td className="booking-breakdown__table-cell--value">
						<FormInputSymbol
							symbol={row.is_rate ? '%' : currencySymbol}
							position={row.is_rate ? 'after' : 'before'}
							value={presentationValueForCommission(row, 'travelnest_fee')}
							readOnly={true}
						/>
					</Td>
					<Td className="booking-breakdown__table-cell--value">
						<FormInputSymbol
							symbol={row.is_rate ? '%' : currencySymbol}
							position={row.is_rate ? 'after' : 'before'}
							value={presentationValueForCommission(row, 'channel_fee')}
							readOnly={true}
						/>
					</Td>
					<Td className="booking-breakdown__table-cell--value">
						<FormInputSymbol
							symbol={row.is_rate ? '%' : currencySymbol}
							position={row.is_rate ? 'after' : 'before'}
							value={presentationValueForCommission(row, 'adjustment_fee')}
							readOnly={true}
						/>
					</Td>
					<Td className="booking-breakdown__table-cell--value">
						<FormInputSymbol
							symbol={row.is_rate ? '%' : currencySymbol}
							position={row.is_rate ? 'after' : 'before'}
							value={presentationValueForCommission(row, 'vat')}
							readOnly={true}
						/>
					</Td>
					<Td className="booking-breakdown__table-cell--value">
						<FormInputSymbol
							symbol={row.is_rate ? '%' : currencySymbol}
							position={row.is_rate ? 'after' : 'before'}
							value={presentationValueForCommission(row, 'total_fee')}
							readOnly={true}
						/>
					</Td>
				</Tr>)}
			</Tbody>
		</Table>
	</Content>;
};
